import { enUS, es, fr, ptBR } from "date-fns/locale";
import {
  Activity,
  CalendarClock,
  CheckCircle2,
  ClipboardList,
  DollarSign,
  FileText,
  Home,
  Inbox as InboxIcon,
  MessageCircle,
  Wrench,
} from "lucide-react";

export function getList(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.items)) return data.items;
  return [];
}

export function formatCurrency(value) {
  const n = typeof value === "string" ? parseFloat(value) : Number(value);
  if (Number.isNaN(n)) return "—";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}

export function getDateFnsLocale(lang) {
  if (!lang) return enUS;
  if (lang.startsWith("es")) return es;
  if (lang.startsWith("pt")) return ptBR;
  if (lang.startsWith("fr")) return fr;
  return enUS;
}

export function priorityRank(priority) {
  const p = (priority || "").toUpperCase();
  if (p === "URGENT" || p === "CRITICAL") return 0;
  if (p === "HIGH") return 1;
  if (p === "MEDIUM") return 2;
  if (p === "LOW") return 3;
  return 4;
}

export function getPriorityVariant(priority) {
  const p = (priority || "").toUpperCase();
  if (p === "URGENT" || p === "CRITICAL") return "destructive";
  if (p === "HIGH") return "warning";
  if (p === "MEDIUM") return "info";
  return "secondary";
}

export function getQueueRowAccent(priority) {
  const p = (priority || "").toUpperCase();
  if (p === "URGENT" || p === "CRITICAL") {
    return "border-l-2 border-destructive/45 bg-destructive/[0.035]";
  }
  if (p === "HIGH") {
    return "border-l-2 border-warning/40 bg-warning/[0.04]";
  }
  return "";
}

export function itemTypeIcon(itemType) {
  switch (itemType) {
    case "APPLICATION":
      return ClipboardList;
    case "MAINTENANCE":
      return Wrench;
    case "CONTRACT":
      return FileText;
    case "VISIT":
      return CalendarClock;
    case "TASK":
      return CheckCircle2;
    case "SUPPORT":
      return MessageCircle;
    default:
      return InboxIcon;
  }
}

export function navigatePathForItem(item) {
  if (!item) return null;
  if (item.sourceEntityId && item.sourceModule) {
    const module = item.sourceModule.toLowerCase();
    const moduleMap = {
      applications: `/applications/${item.sourceEntityId}`,
      calendar: item.sourceEntityId
        ? `/calendar?event=${encodeURIComponent(item.sourceEntityId)}`
        : "/calendar",
      databases: `/databases/${item.sourceEntityId}`,
      maintenance: `/maintenance/${item.sourceEntityId}`,
      organizations: "/organizations",
      properties: `/properties/${item.sourceEntityId}`,
      rentals: `/rentals/${item.sourceEntityId}`,
      support: `/support/${item.sourceEntityId}`,
      tasks: `/tasks/${item.sourceEntityId}`,
      listings: item.sourceEntityId
        ? `/listings?listingId=${encodeURIComponent(item.sourceEntityId)}`
        : "/listings",
      homysign: `/homysign/documents/${item.sourceEntityId}`,
      pipelines: `/pipelines/${item.sourceEntityId}`,
    };
    return moduleMap[module] ?? "/";
  }

  const entityId = item.entityId || item.sourceEntityId || item.id;
  const rawType = (item.entityType || item.sourceModule || item.itemType || item.rawType || item.type || "").toLowerCase();
  if (!rawType) return null;

  if (rawType.includes("prop")) return entityId ? `/properties/${entityId}` : "/properties";
  if (rawType.includes("app")) return entityId ? `/applications/${entityId}` : "/applications";
  if (rawType.includes("maint")) return entityId ? `/maintenance/${entityId}` : "/maintenance";
  if (rawType.includes("contract") || rawType.includes("rental") || rawType.includes("lease")) return entityId ? `/rentals/${entityId}` : "/rentals";
  if (rawType.includes("task")) return entityId ? `/tasks/${entityId}` : "/tasks";
  if (rawType.includes("support")) return entityId ? `/support/${entityId}` : "/support";
  if (rawType.includes("listing")) return entityId ? `/listings?listingId=${encodeURIComponent(entityId)}` : "/listings";
  if (rawType.includes("visit") || rawType.includes("calendar") || rawType.includes("booking")) {
    return entityId ? `/calendar?event=${encodeURIComponent(entityId)}` : "/calendar";
  }
  if (rawType.includes("payment")) return "/rentals/payments";
  if (rawType.includes("sign") || rawType.includes("document")) {
    return entityId ? `/homysign/documents/${entityId}` : "/homysign/templates";
  }
  if (rawType.includes("pipeline") || rawType.includes("dossier")) {
    return entityId ? `/pipelines/${entityId}` : "/pipelines";
  }

  return null;
}

export function resolveActivityCategory(item) {
  const raw = (item?.entityType || item?.itemType || "").toUpperCase();
  if (raw.includes("PAY") || raw.includes("TRANSACT")) {
    return {
      icon: DollarSign,
      labelKey: "inbox:activity.payment",
      get label() {
        return "Payment";
      },
      classes: "border-emerald-500/25 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    };
  }
  if (raw.includes("MAINT") || raw.includes("WORK_ORDER") || raw.includes("TICKET")) {
    return {
      icon: Wrench,
      labelKey: "inbox:activity.maintenance",
      get label() {
        return "Maintenance";
      },
      classes: "border-amber-500/25 bg-amber-500/10 text-amber-600 dark:text-amber-400",
    };
  }
  if (raw.includes("CONTRACT") || raw.includes("LEASE") || raw.includes("RENTAL")) {
    return {
      icon: FileText,
      labelKey: "inbox:activity.contract",
      get label() {
        return "Contract";
      },
      classes: "border-sky-500/25 bg-sky-500/10 text-sky-600 dark:text-sky-400",
    };
  }
  if (raw.includes("APP")) {
    return {
      icon: ClipboardList,
      labelKey: "inbox:activity.application",
      get label() {
        return "Application";
      },
      classes: "border-purple-500/25 bg-purple-500/10 text-purple-600 dark:text-purple-400",
    };
  }
  if (raw.includes("PROP")) {
    return {
      icon: Home,
      labelKey: "inbox:activity.property",
      get label() {
        return "Property";
      },
      classes: "border-primary/25 bg-primary/10 text-primary",
    };
  }
  if (raw.includes("VISIT") || raw.includes("BOOKING")) {
    return {
      icon: CalendarClock,
      labelKey: "inbox:activity.visit",
      get label() {
        return "Visit";
      },
      classes: "border-sky-500/25 bg-sky-500/10 text-sky-600 dark:text-sky-400",
    };
  }
  if (raw.includes("TASK")) {
    return {
      icon: CheckCircle2,
      labelKey: "inbox:activity.task",
      get label() {
        return "Task";
      },
      classes: "border-border bg-muted/80 text-foreground",
    };
  }
  if (raw.includes("SUPPORT")) {
    return {
      icon: MessageCircle,
      labelKey: "inbox:activity.support",
      get label() {
        return "Support";
      },
      classes: "border-primary/25 bg-primary/10 text-primary",
    };
  }
  return {
    icon: Activity,
    labelKey: "inbox:activity.activity",
    get label() {
      return "Activity";
    },
    classes: "border-border bg-muted/60 text-muted-foreground",
  };
}
